/**
 * „DOM-ul s-a așezat" - fereastra de liniște după o schimbare de pagină sau un
 * reveal animat, înainte de a recaptura ce vede vizitatorul.
 *
 * Trăiește separat de `guide.ts` fiindcă au ajuns s-o ceară două locuri cu
 * nevoi diferite: spotlight-ul (care trebuie s-o poată ANULA dacă vizitatorul
 * dă click în altă parte) și conversația vocală (care doar așteaptă). Ambele
 * vor exact aceeași definiție a lui „gata", deci definiția stă într-un
 * singur loc.
 *
 * Fără MutationObserver permanent pe pagina clientului: observăm STRICT în
 * fereastra asta și deconectăm la ieșire, pe orice cale.
 */
export const SETTLE_QUIET_MS = 300;
export const SETTLE_MAX_MS = 2000;

export interface SettleOptions {
  /** Cât de mult trebuie să tacă DOM-ul după ultima mutație. */
  quietMs?: number;
  /** Plafon dur: ieșim oricum, chiar dacă pagina se agită la nesfârșit. */
  maxMs?: number;
  /**
   * Cât așteptăm PRIMA mutație înainte să declarăm că nu mai vine niciuna.
   *
   * Contează când randarea s-a întâmplat deja, sincron, înainte să apucăm să
   * observăm - cazul obișnuit al unui router SPA. Fără asta am aștepta plafonul
   * dur întreg: pe calea vocală, două secunde de tăcere în mijlocul unei
   * conversații. Spotlight-ul NU-l folosește: acolo așteptăm un reveal care
   * poate porni târziu (animație, lazy), iar o ieșire devreme ar recaptura
   * pagina neschimbată.
   */
  firstMutationMs?: number;
}

/** Cheamă `callback` când DOM-ul s-a liniștit. Întoarce anularea (idempotentă). */
export function settleDom(callback: () => void, opts: SettleOptions = {}): () => void {
  let done = false;
  let observer: MutationObserver | null = null;
  let quietTimer: ReturnType<typeof setTimeout> | undefined;

  const cancel = (): void => {
    if (done) return;
    done = true;
    clearTimeout(quietTimer);
    clearTimeout(hardCapTimer);
    observer?.disconnect();
    observer = null;
  };
  const finish = (): void => {
    if (done) return;
    cancel();
    callback();
  };

  const hardCapTimer = setTimeout(finish, opts.maxMs ?? SETTLE_MAX_MS);
  const arm = (): void => {
    clearTimeout(quietTimer);
    quietTimer = setTimeout(finish, opts.quietMs ?? SETTLE_QUIET_MS);
  };
  if (opts.firstMutationMs !== undefined) quietTimer = setTimeout(finish, opts.firstMutationMs);
  observer = new MutationObserver(arm);
  observer.observe(document.body, { childList: true, subtree: true });
  // Deliberat NU armăm aici. Fereastra de liniște trebuie să înceapă la PRIMA
  // mutație, nu la apel: pornită imediat, ar confunda „încă nu s-a întâmplat
  // nimic" cu „totul s-a așezat", iar un randat întârziat peste prag ar veni
  // după ce am recapturat deja pagina veche. Dacă nu vine nicio mutație,
  // plafonul dur închide oricum fereastra.
  return cancel;
}

/** Varianta await-abilă, pentru cine doar așteaptă (conversația vocală). */
export function domSettled(opts: SettleOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    settleDom(resolve, opts);
  });
}
